import { PageIndices, SectionIndices } from '../../common/i-doc/i-doc.registry.ts';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { PageHint } from '../common/PageHint';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import WaterDropIcon from '@mui/icons-material/WaterDrop';

import { ClientDetails } from '@/types/client';
import { CalculatedMetricWithExplanation } from '@/types/metrics';
import { UpdateDailyNormsRequest } from '@/types/settings.types';
import { getClientCalculatedMetrics } from '@services/calculatedMetrics.service';
import { useClientNorms } from '@components/clients/daily-norms/useClientNorms';
import { OwnNormsBadge } from '@components/clients/daily-norms/OwnNormsBadge';
import { MetricExplanationDialog } from '@components/CalculatedMetrics/MetricExplanationDialog';
import { getClientMeasurementHistory } from '@services/metrics.service';
import { DailyNormCard } from '@components/clients/dashboard/DailyNormCard.tsx';
import { ClientProfileHeader } from '@components/clients/dashboard/ClientProfileHeader.tsx';
import { ClientMainGoalCard } from '@components/clients/dashboard/ClientMainGoalCard.tsx';
import { CollaborationPlanCard } from '@components/clients/dashboard/CollaborationPlanCard.tsx';
import { RecommendationsProgressCard } from '@components/clients/dashboard/RecommendationsProgressCard.tsx';
import { ClientMetricsCards } from '@components/clients/dashboard/ClientMetricsCards.tsx';
import { collaborationPlanService, CollaborationStep } from '@services/collaboration-plan.service';
import { getClientMainGoal, ClientMainGoal } from '@services/client-main-goal.service';
import { DailyNormEditDialog, DailyNormEditConfig } from '@components/clients/dashboard/DailyNormEditDialog.tsx';
import { MacrosEditDialog, MacroKey } from '@components/clients/dashboard/MacrosEditDialog.tsx';
import { toastService } from '@services/toast.service';
import { ProteinIcon, FatIcon, CarbsIcon } from '@components/icons';
import { dailyNormCard, dailyNormEditDialog } from '@/styles/tokens';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';

interface ClientDashboardTabProps {
  clientId: string;
  client: ClientDetails;
}

const isWeightMeasurement = (name: string) =>
  name.toLowerCase().includes('вага') || name.toLowerCase().includes('weight');

type NormKey = 'caloriesIntake' | 'waterMl' | MacroKey;

// Norm formulas -> calculation_details section holding their step.
type FormulaMetric = 'bmr' | 'amr' | 'wdna' | 'amdr';
const FORMULA_STEPS: Record<FormulaMetric, { section: 'calories' | 'water' | 'nutrients' }> = {
  bmr: { section: 'calories' },
  amr: { section: 'calories' },
  wdna: { section: 'water' },
  amdr: { section: 'nutrients' },
};

const isFormulaMetric = (metric: string): metric is FormulaMetric => metric in FORMULA_STEPS;

const isMacroKey = (key: NormKey): key is MacroKey => key === 'proteinG' || key === 'fatG' || key === 'carbsG';

// Ключі перекладів - clientProfile.dailyNorms.*
interface NormConfig {
  key: NormKey;
  labelKey: string;
  titleKey: string;
  unitKey: string;
  icon: React.ReactElement;
  /** Модалка редагування однієї норми; Б/Ж/В редагуються спільною MacrosEditDialog. */
  edit?: {
    fieldLabelKey: string;
    recommendedIcon: React.ReactElement;
    recommendedColor: string;
    /** Формули (BMR, AMR, WDNA) - кнопки пояснення в блоці рекомендованої норми; перша - у підказці «…за формулою». */
    formulas: FormulaMetric[];
    /** Своє пояснення замість «Розраховано за формулою …» (норма - ланцюжок кроків, а не одна формула). */
    hintKey?: string;
  };
}

const NORMS: NormConfig[] = [
  {
    key: 'caloriesIntake', labelKey: 'norm.calories', titleKey: 'normName.calories', unitKey: 'unit.kcal', icon: <LocalFireDepartmentIcon />,
    edit: {
      fieldLabelKey: 'edit.caloriesField',
      recommendedIcon: <BoltRoundedIcon />, recommendedColor: dailyNormEditDialog.recommendedCalories,
      formulas: ['bmr', 'amr'],
      hintKey: 'edit.caloriesHint',
    },
  },
  {
    key: 'waterMl', labelKey: 'norm.water', titleKey: 'normName.water', unitKey: 'unit.ml', icon: <WaterDropIcon />,
    edit: {
      fieldLabelKey: 'edit.waterField',
      recommendedIcon: <WaterDropIcon />, recommendedColor: dailyNormEditDialog.recommendedWater,
      formulas: ['wdna'],
    },
  },
  { key: 'proteinG', labelKey: 'norm.protein', titleKey: 'norm.protein', unitKey: 'unit.g', icon: <ProteinIcon /> },
  { key: 'fatG', labelKey: 'norm.fat', titleKey: 'norm.fat', unitKey: 'unit.g', icon: <FatIcon /> },
  { key: 'carbsG', labelKey: 'norm.carbs', titleKey: 'norm.carbs', unitKey: 'unit.g', icon: <CarbsIcon /> },
];

export const ClientDashboardTab: React.FC<ClientDashboardTabProps> = ({ clientId, client }) => {
  const g = client.general;
  const { t: tn } = useTranslation(undefined, { keyPrefix: 'clientProfile.dailyNorms' });

  const [metrics, setMetrics] = useState<CalculatedMetricWithExplanation[]>([]);
  const [latestWeight, setLatestWeight] = useState<number | null>(null);
  const [mainGoal, setMainGoal] = useState<ClientMainGoal | null>(null);

  const [metricsLoading, setMetricsLoading] = useState(true);
  const [metricsError, setMetricsError] = useState(false);
  const [collaborationPlan, setCollaborationPlan] = useState<CollaborationStep[]>([]);
  const [collaborationPlanTotal, setCollaborationPlanTotal] = useState(0);
  const [editingNorm, setEditingNorm] = useState<NormKey | null>(null);
  const [savingNorm, setSavingNorm] = useState(false);
  const [explainedMetric, setExplainedMetric] = useState<string | null>(null);
  // Ефективна норма (власна експерта ?? стандартна) - картки; стандартна - «Рекомендована норма».
  const clientNorms = useClientNorms(clientId);
  const { effective: norms, standard: standardNorms, loading: normsLoading, error: normsError } = clientNorms;

  useEffect(() => {
    getClientCalculatedMetrics(clientId)
      .then((data) => setMetrics(data.filter((m) => m.status === 'calculated')))
      .catch(() => setMetricsError(true))
      .finally(() => setMetricsLoading(false));

    getClientMainGoal(clientId).then(setMainGoal).catch(() => {});

    getClientMeasurementHistory(clientId, { limit: 90 })
      .then((items) => {
        const weightItems = items
          .filter((i) => isWeightMeasurement(i.measurementType.name))
          .sort((a, b) => new Date(a.measuredAt).getTime() - new Date(b.measuredAt).getTime());

        if (weightItems.length > 0) {
          setLatestWeight(weightItems[weightItems.length - 1].value);
        }
      })
      .catch(() => {});

    collaborationPlanService.getPlan(clientId).then(({ items, total }) => {
      setCollaborationPlan(items);
      setCollaborationPlanTotal(total);
    }).catch(() => {});
  }, [clientId]);

  const reloadCollaborationPlan = () => {
    collaborationPlanService.getPlan(clientId).then(({ items, total }) => {
      setCollaborationPlan(items);
      setCollaborationPlanTotal(total);
    }).catch(() => {});
  };

  const handleCollaborationStepComplete = (stepId: number) => {
    collaborationPlanService.markDone(clientId, stepId).then(() => {
      reloadCollaborationPlan();
      toastService.success('Задачу позначено виконаною');
    }).catch(() => {
      toastService.error('Не вдалося позначити задачу виконаною');
    });
  };

  const handleCollaborationStepDelete = (stepId: number) => {
    collaborationPlanService.remove(clientId, stepId).then(() => {
      reloadCollaborationPlan();
      toastService.success('Задачу видалено з плану співпраці');
    }).catch(() => {
      toastService.error('Не вдалося видалити задачу');
    });
  };

  const editConfig = useMemo<DailyNormEditConfig | null>(() => {
    if (!norms || !editingNorm) return null;
    const norm = NORMS.find((n) => n.key === editingNorm);
    if (!norm?.edit) return null;
    const { fieldLabelKey, formulas, hintKey, ...edit } = norm.edit;
    return {
      title: tn(norm.titleKey),
      unit: tn(norm.unitKey),
      value: norms[norm.key],
      recommended: standardNorms?.[norm.key] ?? null,
      fieldLabel: tn(fieldLabelKey),
      recommendedHint: hintKey ? tn(hintKey) : tn('edit.formulaHint', { formula: formulas[0].toUpperCase() }),
      formulaLabels: formulas.map((f) => f.toUpperCase()),
      ...edit,
    };
  }, [norms, standardNorms, editingNorm, tn]);

  const macrosGrams = useMemo(
    () => norms && { proteinG: norms.proteinG, fatG: norms.fatG, carbsG: norms.carbsG },
    [norms],
  );
  const recommendedMacros = useMemo(
    () =>
      standardNorms && {
        grams: { proteinG: standardNorms.proteinG, fatG: standardNorms.fatG, carbsG: standardNorms.carbsG },
        calories: standardNorms.caloriesIntake,
      },
    [standardNorms],
  );

  // Client-specific explanation: the formula's own step from the standard norms calculation (step name ends with
  // "(BMR)", "(AMR)", ...). Not a norm formula or no step - the dialog shows the calculator's generic example.
  const clientSteps = useMemo(() => {
    if (!explainedMetric || !isFormulaMetric(explainedMetric)) return null;
    const steps = standardNorms?.calculationDetails?.[FORMULA_STEPS[explainedMetric].section]?.steps ?? [];
    const own = steps.filter((step) => step.name.endsWith(`(${explainedMetric.toUpperCase()})`));
    return own.length ? own : null;
  }, [explainedMetric, standardNorms]);

  const saveNorms = async (changes: UpdateDailyNormsRequest) => {
    setSavingNorm(true);
    try {
      await clientNorms.saveOwn(changes);
      setEditingNorm(null);
      toastService.success(tn('saved'));
    } catch {
      toastService.error(tn('saveError'));
    } finally {
      setSavingNorm(false);
    }
  };

  const fullName = [g.name, g.surname].filter(Boolean).join(' ');
  const age = g.bornYear ? new Date().getFullYear() - g.bornYear : null;


  return (
    <Box sx={{ px: 2.5, pb: 2.5 }} data-i-doc={PageIndices.EXPERT_CLIENT_DASHBOARD}>
      {/* New dashboard widgets (redesign, mock stage) - above the existing ones. */}
      <ClientProfileHeader
        clientId={String(clientId)}
        fullName={fullName}
        email={g.email ?? null}
        phone={g.phone ?? null}
        avatar={g.avatar ?? null}
        age={age}
        height={g.height ?? null}
        weight={latestWeight}
      />
      <PageHint iDoc={PageIndices.EXPERT_CLIENT_DASHBOARD} />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '353px minmax(0, 1fr)' }, gap: 2, alignItems: 'stretch', mb: 3 }}>
        <ClientMainGoalCard goal={mainGoal} />
        <CollaborationPlanCard
          items={collaborationPlan}
          totalCount={collaborationPlanTotal}
          clientId={clientId}
          clientName={fullName || 'клієнта'}
          onComplete={handleCollaborationStepComplete}
          onDelete={handleCollaborationStepDelete}
          onChanged={reloadCollaborationPlan}
        />
      </Box>
      <RecommendationsProgressCard clientId={String(clientId)} />

      {/* ── Денні норми ── */}
      <Box data-i-doc={SectionIndices.EXPERT_CLIENT_DAILY_NORMS}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <LocalFireDepartmentIcon sx={{ fontSize: 20, color: dailyNormCard.titleIcon }} />
          <Typography sx={{ fontSize: 16, lineHeight: '19px', fontWeight: 700, color: dailyNormCard.text }}>
            {tn('title')}
          </Typography>
          {clientNorms.expert?.effectiveFrom && <OwnNormsBadge />}
        </Box>
        {normsLoading ? (
          <CircularProgress size={24} sx={{ mb: 2 }} />
        ) : normsError ? (
          <Alert severity="warning" sx={{ mb: 2 }}>{tn('loadError')}</Alert>
        ) : norms ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' },
              gap: 2,
              mb: 3,
            }}
          >
            {NORMS.map((n) => (
              <DailyNormCard
                key={n.key}
                icon={n.icon}
                label={tn(n.labelKey)}
                value={norms[n.key]}
                unit={tn(n.unitKey)}
                onEdit={() => setEditingNorm(n.key)}
              />
            ))}
          </Box>
        ) : null}
      </Box>

      <ClientMetricsCards metrics={metrics} loading={metricsLoading} error={metricsError} onInfo={setExplainedMetric} />

      {/* Зберігається власна норма експерта; порожнє поле - скинути до стандартної. */}
      <DailyNormEditDialog
        open={!!editingNorm}
        onClose={() => setEditingNorm(null)}
        config={editConfig}
        saving={savingNorm}
        onSave={(value) => { if (editingNorm) saveNorms({ [editingNorm]: value }); }}
        onFormulaClick={(label) => setExplainedMetric(label.toLowerCase())}
      />
      <MacrosEditDialog
        open={!!editingNorm && isMacroKey(editingNorm)}
        onClose={() => setEditingNorm(null)}
        grams={macrosGrams}
        calories={norms?.caloriesIntake ?? null}
        recommended={recommendedMacros}
        focusKey={editingNorm && isMacroKey(editingNorm) ? editingNorm : null}
        saving={savingNorm}
        onSave={(grams) => saveNorms(grams)}
        onFormulaClick={(label) => setExplainedMetric(label.toLowerCase())}
      />
      <MetricExplanationDialog
        open={!!explainedMetric}
        onClose={() => setExplainedMetric(null)}
        metric={explainedMetric}
        clientSteps={clientSteps}
      />
    </Box>
  );
};
